import axios from 'axios';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import {
  TwoFactorVerifyDTO,
  TwoFactorVerifyResponse,
  UserDTO,
} from '@/types/api-types';

import { useWebSocketStore } from '@/stores/websocketStore';

import { useWsClientStore } from '@/services/ws-client';

import api from '@/utils/api';

import { API_ROUTES } from '@/constants/api-constants';
import { BASE_URL_API } from '@/constants/global-constants';
import { publicRoutes } from '@/constants/route-settings';

interface TwoFactorResponse {
  is2FAEnabled: boolean;
  totpKey?: string;
  totpSecret?: string;
  uuid: string;
}

interface AuthEditRequestDTO {
  uuid: string;
  email: string;
  user_type: string;
  phone_number: string;
}

interface UserCreateAdminRequest {
  email: string;
  user_type: string;
  is_active: boolean;
  first_name: string;
  last_name: string;
  phone_number: string;
}

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: UserDTO | null;
  isAuthenticated: boolean;
  twoFactorData: TwoFactorResponse | null;

  signIn: (username: string, password: string) => Promise<TwoFactorResponse>;
  verifyTwoFactor: ({
    otp,
    username,
    password,
  }: TwoFactorVerifyDTO) => Promise<TwoFactorVerifyResponse>;
  signOut: () => void;
  refreshAccessToken: () => Promise<string>;
  resetPassword: (email: string) => Promise<void>;
  reset2FA: (id: string) => Promise<void>;
  updateUserActiveStatus: (user_id: string, status: boolean) => Promise<void>;
  updateUserInfo: (userData: AuthEditRequestDTO) => Promise<void>;
  resetToken: (uuid: string) => Promise<{ static_token: string }>;
  createUserAdmin: (
    userData: UserCreateAdminRequest,
  ) => Promise<{ uuid: string; staticToken: string } | null>;
  getCurrentUser: () => UserDTO | null;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      expiresIn: null,
      twoFactorData: null,

      signIn: async (username: string, password: string) => {
        try {
          const response = await api.post(
            `${BASE_URL_API}${API_ROUTES.AUTH.LOGIN}`,
            {
              username,
              password,
            },
          );

          const { data } = response;

          set({ twoFactorData: data });
          return data;
        } catch (error) {
          if (axios.isAxiosError(error)) {
            if (error.response?.status === 401) {
              throw new Error('Invalid username or password');
            }
            throw new Error(error.response?.data?.message || 'Login failed');
          }
          throw new Error('An unexpected error occurred');
        }
      },

      verifyTwoFactor: async ({ otp, username, password }) => {
        const { twoFactorData } = get();
        if (!twoFactorData) {
          throw new Error('No 2FA data available');
        }

        try {
          const response = await api.post<TwoFactorVerifyResponse>(
            `${BASE_URL_API}${API_ROUTES.AUTH.TOTP_VERIFY}`,
            {
              otp,
              username,
              password,
            },
          );
          const newState = {
            user: {
              role: response.data.role,
              username: response.data.username,
              isActive: response.data.isActive,
              uuid: response.data.uuid,
            },
            token: response.data.accessToken,
            refreshToken: response.data.refreshToken,
            isAuthenticated: true,
            twoFactorData: null,
          };

          set(newState);

          return response.data;
        } catch (error) {
          if (axios.isAxiosError(error)) {
            throw new Error(
              error.response?.data?.message || 'Verification failed',
            );
          }
          throw new Error('An unexpected error occurred');
        }
      },

      signOut: () => {
        // Disconnect WebSocket
        useWsClientStore.getState().disconnect();

        set({
          token: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
          twoFactorData: null,
        });
        // Force a hard redirect to login page
        window.location.replace(publicRoutes.login);
      },

      refreshAccessToken: async () => {
        const { refreshToken, token } = get();
        if (!refreshToken) {
          window.location.replace(publicRoutes.login);
        }

        try {
          const response = await api.post(
            `${BASE_URL_API}${API_ROUTES.AUTH.REFRESH}`,
            {
              refreshToken,
              accessToken: token,
            },
          );
          const { accessToken, refreshToken: newRefreshToken } = response.data;

          set({ token: accessToken, refreshToken: newRefreshToken });

          // Reconnect WebSocket with the new token
          await useWebSocketStore.getState().connect(accessToken);

          return accessToken;
        } catch (error) {
          // If refresh token is invalid, sign out which will redirect to login
          get().signOut();
          throw new Error('Failed to refresh token');
        }
      },

      resetPassword: async (email: string) => {
        const response = await api.post(
          `${BASE_URL_API}${API_ROUTES.AUTH.RESET_PASSWORD}`,
          {
            username: email,
          },
        );

        return response.data;
      },
      reset2FA: async (uuid: string) => {
        const response = await api.post(
          `${BASE_URL_API}${API_ROUTES.AUTH.RESET_TOTP}`,
          {
            uuid,
          },
        );
        return response.data;
      },
      updateUserActiveStatus: async (user_id: string, status: boolean) => {
        const response = await api.post(
          `${BASE_URL_API}${API_ROUTES.AUTH.UPDATE_USER}`,
          {
            user_id,
            status,
          },
        );
        return response.data;
      },
      // type AuthEditRequestDTO struct {
      //   Username    string `json:"email" bson:"Email" validate:"required,email"`
      //   Role        string `json:"user_type" bson:"user_type" validate:"required"`
      //   FirstName   string `json:"first_name" bson:"first_name" validate:"required"`
      //   LastName    string `json:"last_name" bson:"last_name" validate:"required"`
      //   PhoneNumber string `json:"phone_number" bson:"phone_number" validate:"required,e164"`
      //  }
      updateUserInfo: async (userData: AuthEditRequestDTO) => {
        const response = await api.post(
          `${BASE_URL_API}${API_ROUTES.AUTH.UPDATE_USER_INFO}`,
          userData,
        );
        return response.data;
      },
      resetToken: async (user_id: string) => {
        const response = await api.post<{ static_token: string }>(
          `${BASE_URL_API}${API_ROUTES.AUTH.RESET_TOKEN}`,
          { user_id },
        );
        return response.data;
      },

      createUserAdmin: async (userData: UserCreateAdminRequest) => {
        const response = await api.post<{
          uuid: string;
          staticToken: string;
        } | null>(`${BASE_URL_API}${API_ROUTES.AUTH.CREATE_USER}`, userData);
        return response.data;
      },
      getCurrentUser: () => {
        return get().user;
      },
    }),

    {
      name: 'auth-storage',

      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        twoFactorData: state.twoFactorData,
      }),
    },
  ),
);
